import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Truck, MapPin, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
        setOrder(response.data);
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const getStatusStep = (status) => {
    switch (status) {
      case "pending":
        return 0;
      case "paid":
        return 1;
      case "processing":
        return 2;
      case "shipped":
        return 3;
      case "delivered":
        return 4;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-black border-t-accent-pink animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl mb-4">ORDINE NON TROVATO</h1>
          <Link to="/profilo" className="btn-brutal">
            TORNA AL PROFILO
          </Link>
        </div>
      </div>
    );
  }

  const currentStep = getStatusStep(order.status);
  const itemsSubtotal =
    order.subtotal ??
    (order.items || []).reduce((sum, item) => sum + Number(item.subtotal || 0), 0);
  const shippingCost =
    order.shipping_cost != null
      ? Number(order.shipping_cost)
      : Math.max(Number(order.total || 0) - Number(itemsSubtotal || 0), 0);
  const orderTotal =
    order.total != null ? Number(order.total) : Number(itemsSubtotal || 0) + Number(shippingCost || 0);

  const steps = [
    { label: "Ordine ricevuto", icon: Package },
    { label: "Pagamento confermato", icon: CheckCircle },
    { label: "In preparazione", icon: Package },
    { label: "Spedito", icon: Truck },
    { label: "Consegnato", icon: MapPin },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="order-detail-page">
      <div className="border-b-2 border-black py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/profilo" className="inline-flex items-center font-mono text-sm hover:text-accent-pink">
            <ArrowLeft size={16} className="mr-2" />
            TORNA AL PROFILO
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl mb-2">ORDINE</h1>
          <p className="font-mono text-gray-500">#{order.order_id}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border-4 border-black p-6">
              <h2 className="font-heading text-2xl mb-6">STATO ORDINE</h2>

              <div className="space-y-4">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStep;
                  const isCurrent = index === currentStep;

                  return (
                    <div key={index} className="flex items-center gap-4">
                      <div
                        className={`
                        w-12 h-12 border-2 border-black flex items-center justify-center flex-shrink-0
                        ${isCompleted ? "bg-accent-green" : "bg-gray-100"}
                        ${isCurrent ? "shadow-brutal" : ""}
                      `}
                      >
                        <Icon size={24} className={isCompleted ? "text-black" : "text-gray-400"} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-body font-semibold ${isCompleted ? "text-black" : "text-gray-400"}`}>{step.label}</p>
                        {index === 3 && order.tracking_number && isCompleted && (
                          <p className="font-mono text-sm text-accent-pink">Tracking: {order.tracking_number}</p>
                        )}
                      </div>
                      {isCompleted && <CheckCircle size={20} className="text-accent-green" />}
                    </div>
                  );
                })}
              </div>

              {order.tracking_number && (
                <div className="mt-6 p-4 bg-accent-yellow border-2 border-black">
                  <p className="font-mono text-sm">
                    <strong>TRACKING:</strong> {order.tracking_number}
                  </p>
                  <p className="font-body text-sm mt-1 text-gray-700">Usa questo codice per tracciare la tua spedizione</p>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="border-4 border-black p-6"
            >
              <h2 className="font-heading text-2xl mb-6">ARTICOLI</h2>

              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex gap-4 border-b-2 border-black pb-4 last:border-0 last:pb-0">
                    <div className="w-20 h-20 border-2 border-black overflow-hidden flex-shrink-0">
                      <img src={item.product?.image_url} alt={item.product?.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg truncate">{item.product?.name}</h3>
                      <p className="font-mono text-sm text-gray-500">
                        {item.size} / {item.color}
                      </p>
                      <p className="font-mono text-sm">Qta: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold">EUR {Number(item.subtotal || 0).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="border-4 border-black p-6"
            >
              <h2 className="font-heading text-xl mb-4">RIEPILOGO</h2>

              <div className="space-y-3 border-b-2 border-black pb-4 mb-4">
                <div className="flex justify-between font-body">
                  <span>Subtotale</span>
                  <span>EUR {itemsSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-body">
                  <span>Spedizione</span>
                  {shippingCost === 0 ? <span className="text-green-600">GRATIS</span> : <span>EUR {shippingCost.toFixed(2)}</span>}
                </div>
              </div>

              <div className="flex justify-between">
                <span className="font-heading text-xl">TOTALE</span>
                <span className="font-mono text-2xl font-bold">EUR {orderTotal.toFixed(2)}</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="border-4 border-black p-6"
            >
              <h2 className="font-heading text-xl mb-4">INDIRIZZO SPEDIZIONE</h2>

              {order.shipping_address ? (
                <div className="font-body text-gray-700 space-y-1">
                  <p className="font-semibold">{order.shipping_address.full_name}</p>
                  <p>{order.shipping_address.address}</p>
                  <p>
                    {order.shipping_address.postal_code} {order.shipping_address.city}
                  </p>
                  <p>{order.shipping_address.country}</p>
                  {order.shipping_address.phone && <p className="font-mono text-sm mt-2">Tel: {order.shipping_address.phone}</p>}
                </div>
              ) : (
                <p className="font-body text-gray-500">Indirizzo non disponibile</p>
              )}
            </motion.div>

            <div className="border-2 border-black p-4 bg-gray-50">
              <p className="font-mono text-sm text-gray-500">DATA ORDINE</p>
              <p className="font-body font-semibold">
                {new Date(order.created_at).toLocaleDateString("it-IT", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
